


import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VendorAccount_Service, Vendor_Service, UserPermission_Service } from 'legalreflex-lib';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

const iManage: string = "iManage";

@Component({
    templateUrl: 'importDocument.html',
    styleUrls: ['./importDocument.scss']
})
export class ImportDocumentDialog implements OnInit, OnDestroy {

    public importedFiles: BehaviorSubject<any>;
    public documentSelection: BehaviorSubject<any> = new BehaviorSubject(null);

    public workspaceMap: Map<String, WorkspaceRow[]> = new Map();
    public folderMap: Map<String, FolderRow[]> = new Map();
    
    public libraries: LibraryRow[] = [];
    public selectedDocumentsList: DocumentDetails[] = [];
    public drilldown: string[] = [];
    
    public importButtonText: string = "";
    public successAccountUUID: string = "";
    public nextTooltip: string = "";
    
    public index: number = 0;
    public fileSize: number = 0;
    public fileSizeLimit: number = 24576000; 
    
    public importing: boolean = false;
    public importComplete: boolean = false;
    public cancelled: boolean = false;
    public loading: boolean = true;
    public noBindings: boolean = false;
    public badBinding: boolean = false;
    public error: boolean = false;

    private destroy$: Subject<void> = new Subject<void>();
    private getIManageWorkspaces$: BehaviorSubject<any> = new BehaviorSubject(null);
    private getIManageUserId$: BehaviorSubject<any> = new BehaviorSubject(null);
    private getIManageWorkspaceContents$: BehaviorSubject<any> = new BehaviorSubject(null);
    
    public refresh$: Subject<void> = new Subject<void>();

constructor(
    @Inject(MAT_DIALOG_DATA) public data: {successAccountUUID: string, importedFiles: BehaviorSubject<any>, fileSize: number},
    private dialogRef : MatDialogRef<ImportDocumentDialog>,
    protected vendorAccountService: VendorAccount_Service,
    protected vendorService: Vendor_Service,
    protected userPermissionService: UserPermission_Service
    ) {
        // console.log("importDocuments:", data);
        this.successAccountUUID = data.successAccountUUID;
        this.importedFiles = data.importedFiles;
        this.fileSize = data.fileSize;
        this.importButtonText = `No Files Selected`;
        let drilldown = localStorage.getItem("folderDrilldown");
        if(typeof drilldown == "string"){
            this.drilldown = drilldown.split(",");
        }  
    }

    ngOnInit(): void {

        // Called in ExpandWorkspace()
        this.getIManageWorkspaceContents$
            .pipe(
                filter((getWorkspaceContents: any) => getWorkspaceContents !== null),
                switchMap((getWorkspaceContents: any) => getWorkspaceContents),
                takeUntil(this.destroy$)
            )
            .subscribe();

        // Called on click in template
        this.refresh$
            .pipe(
                switchMap(() => this.loadData()),
                takeUntil(this.destroy$)
            )
            .subscribe();

        // Called in loadData()
        this.getIManageWorkspaces$
            .pipe(
                filter((getWorkspaces: any) => getWorkspaces !== null),
                switchMap((getWorkspaces: any) => getWorkspaces),
                takeUntil(this.destroy$)
            )
            .subscribe();

        // Called in loadData()
        this.getIManageUserId$
            .pipe(
                filter((getUserId: any) => getUserId !== null),
                switchMap((getUserId: any) => getUserId),
                takeUntil(this.destroy$)
            )
            .subscribe();

        this.vendorAccountService
            .getVendorAccountsByUsernameAndVendor({
                username: this.userPermissionService.loggedInUser!.username,
                vendorName: iManage,
            })
            .pipe(
                switchMap((res: any) => {
                    this.noBindings = res.length < 1;
                    if (!this.noBindings) return this.loadData();
                    return of();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe();

        this.documentSelection.pipe(takeUntil(this.destroy$)).subscribe((docSelected: DocumentDetails) => {
            if (docSelected) {
                this.selectedDocumentsList.forEach((doc) => {
                    if (docSelected && doc.workspace_id !== docSelected.workspace_id) doc.selected = false;
                });
                docSelected.downloaded = 0;
                if (!docSelected.selected) {
                    this.fileSize -= Number(docSelected.size);
                    this.selectedDocumentsList = this.selectedDocumentsList.filter((doc) => doc.id != docSelected.id);
                } else {
                    this.fileSize += Number(docSelected.size);
                    this.selectedDocumentsList = this.selectedDocumentsList.filter((doc) => doc.workspace_id == docSelected.workspace_id);
                    this.selectedDocumentsList.push(docSelected);
                }
                if (this.selectedDocumentsList.length) {
                    this.importButtonText = `Import ${this.selectedDocumentsList.length} File${this.selectedDocumentsList.length > 1 ? "s" : ""}`;
                } else {
                    this.importButtonText = "No Files Selected";
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ExpandLibrary(libraryName: string){
        this.libraries.forEach((libRow: LibraryRow) => {
            this.drilldown = [];
            this.workspaceMap.get(libraryName.toLowerCase())?.forEach((workRow: WorkspaceRow) =>{
                workRow.expanded = false;
            });
            if(libRow.libraryName.toLowerCase == libraryName.toLowerCase){
                libRow.expanded = !libRow.expanded;
                if(libRow.expanded)
                    this.drilldown.push(libraryName);
                else
                    this.drilldown = this.drilldown.filter(a => a != libraryName);
                this.saveDrilldown();
            }
        })
    }
    
    ExpandWorkspace(libraryName: string, workspaceId: string, skipDrilldown: boolean = false){
        this.folderMap.delete(workspaceId);
        this.workspaceMap.get(libraryName.toLowerCase())?.forEach((workRow: WorkspaceRow) =>{
            if(workRow.workspaceId == workspaceId){
                workRow.expanded = !workRow.expanded;
                if(!skipDrilldown){
                    if(workRow.expanded)
                        this.drilldown = [libraryName, workspaceId];
                    else
                        this.drilldown = this.drilldown.filter(a => a != workRow.workspaceId);
                    this.saveDrilldown();
                }
                if(workRow.expanded){
                    this.getIManageWorkspaceContents$.next(
                        this.vendorAccountService
                            .getiManageWorkspaceContents(workRow.authToken, workRow.customerId, workRow.libraryId, workRow.workspaceId)
                            .pipe(
                                tap((ret: any) => {
                                    if (ret) {
                                        this.folderMap.delete(workspaceId);
                                        ret.result.data.forEach((folderData: any) => {
                                            if (!this.folderMap.get(workspaceId)) {
                                                this.folderMap.set(workspaceId, [
                                                    {
                                                        authToken: workRow.authToken,
                                                        customerId: workRow.customerId,
                                                        libraryId: workRow.libraryId,
                                                        folderName: folderData.name,
                                                        folderId: folderData.id,
                                                        expanded: false,
                                                    },
                                                ]);
                                            } else {
                                                this.folderMap.get(workspaceId)?.push({
                                                    authToken: workRow.authToken,
                                                    customerId: workRow.customerId,
                                                    libraryId: workRow.libraryId,
                                                    folderName: folderData.name,
                                                    folderId: folderData.id,
                                                    expanded: false,
                                                });
                                            }
                                        });
                                    }
                                })
                            )
                    );
                }
            }
        });
    }

    close(){
        this.dialogRef.close(false);
    }

    cancelDownload(){
        this.cancelled = true;
        this.dialogRef.close(false);
    }

    import(){
        this.dialogRef.disableClose = true;
        this.importing = true;

        this.dialogRef.disableClose = true;
        this.importing = true;
  
        let place = 0;
        this.selectedDocumentsList[place].downloaded++
        this.selectedDocumentsList.forEach((d: DocumentDetails) => {
            // console.log("Doc:", d);
            // console.log("wm:", this.workspaceMap);
            this.importedFiles.next({
                auth_token: d.auth_token,
                refresh_token: d.refresh_token,
                vendorId: d.id,
                name: d.name,
                fileName: d.fileName,
                extension: d.extension,
                sequenceIndex: 0,
                vendorDownloadURI: 'https://cloudimanage.com/work/web/api/v2/customers/' + d.customerId + '/libraries/' + d.libraryId + '/documents/' + d.id + '/download'
            })
            this.selectedDocumentsList[place++].downloaded++;
            if(this.selectedDocumentsList[place])
                this.selectedDocumentsList[place].downloaded++;
        })

        this.importComplete = true;
    }

    public loadData(){
        if (this.successAccountUUID?.trim().length) {
            return this.vendorAccountService.getiManageAuthTokens(this.successAccountUUID.trim()).pipe(
                map((res: { email: string; authToken: string }) => {
                    if (res) {
                        let authTokens: string[] = [];
                        let emails: string[] = [];
                        // console.log("res:", res);
                        if (!res.authToken) {
                            this.noBindings = true;
                            this.badBinding = true;
                            return;
                        }
                        this.noBindings = false;
                        this.badBinding = false;
                        authTokens.push(res.authToken);
                        emails.push(res.email);
                        return this.vendorAccountService.getiManageDirectoryInformation(authTokens).pipe(
                            tap((data: CustomerData[]) => {
                                if (data && data.length) {
                                    let dataIdx = 0;
                                    data.forEach((customerInfo: CustomerData) => {
                                        customerInfo.libraries.libraryIds.forEach((lid: string) => {
                                            this.libraries.push({ libraryName: lid, expanded: false });
                                        });
                                        this.loading = false;
                                        let curAuthToken: string = authTokens[dataIdx];
                                        let curEmail: string = emails[dataIdx++];
                                        this.getIManageUserId$.next(
                                            this.vendorAccountService
                                                .getiManageUserId(curAuthToken, customerInfo.customerId.customerId, curEmail)
                                                .pipe(
                                                    tap((userId: any) => {
                                                        customerInfo.libraries.libraryIds.forEach((libraryId: string) => {
                                                            this.getIManageWorkspaces$.next(
                                                                this.vendorAccountService
                                                                    .getiManageWorkspaces(
                                                                        curAuthToken,
                                                                        customerInfo.customerId.customerId,
                                                                        libraryId,
                                                                        userId.message
                                                                    )
                                                                    .pipe(
                                                                        tap((workspaces: any) => {
                                                                            if (workspaces) {
                                                                                workspaces.data.results.forEach((workspace: any) => {
                                                                                    if (!this.workspaceMap.get(workspace.database.toLowerCase())) {
                                                                                        this.workspaceMap.set(workspace.database.toLowerCase(), [
                                                                                            {
                                                                                                authToken: curAuthToken,
                                                                                                customerId: customerInfo.customerId.customerId,
                                                                                                libraryId: libraryId,
                                                                                                workspaceName: workspace.name,
                                                                                                workspaceId: workspace.id,
                                                                                                hasSubfolders: workspace.has_subfolders,
                                                                                                expanded: false,
                                                                                            },
                                                                                        ]);
                                                                                    } else {
                                                                                        this.workspaceMap
                                                                                            .get(workspace.database.toLowerCase())
                                                                                            ?.push({
                                                                                                authToken: curAuthToken,
                                                                                                customerId: customerInfo.customerId.customerId,
                                                                                                libraryId: libraryId,
                                                                                                workspaceName: workspace.name,
                                                                                                workspaceId: workspace.id,
                                                                                                hasSubfolders: workspace.has_subfolders,
                                                                                                expanded: false,
                                                                                            });
                                                                                    }
                                                                                    if (this.drilldown.includes(workspace.id))
                                                                                        this.ExpandWorkspace(libraryId, workspace.id, true);
                                                                                });
                                                                            }
                                                                        })
                                                                    )
                                                            );
                                                        });
                                                    })
                                                )
                                        );
                                    });
                                } else {
                                    this.error = true;
                                    console.log("Didn't find any customerId's or libraries bound to this success/vendor account.");
                                }
                            })
                        );
                    } else {
                        throw "Didn't find an auth token for the success account.";
                    }
                }),
                catchError((err) => {
                    this.error = true;
                    console.log("Didn't find an auth token for the success account.");
                    return of();
                }),
                switchMap((authTokens: any) => authTokens),
                tap({
                    error: (err) => {
                        console.log("Error:", err);
                        this.error = true;
                    },
                })
            );
        } else {
            this.error = true;
            console.log("No successAccountUUID provided.");
            return of();
        }
    }

    public validateNext(){
        // console.log("validateNext:", this.selectedDocumentsList.length, ":l|fs:", this.fileSize, "|", this.fileSizeLimit);
        if(!this.selectedDocumentsList.length){
            this.nextTooltip = "Please select at least 1 file to import.";
            this.importButtonText = "No Files Selected";
            return false;
        }
        // if(this.fileSize > this.fileSizeLimit){
        //     this.nextTooltip = "The file(s) selected exceed the maximum (24mb) total file size."
        //     this.importButtonText = "File Size Limit Exceeded"
        //     return false;
        // }
        return true;
    }

    public saveDrilldown(){
        let drilldown: string = "";
        this.drilldown.forEach( a => {
            drilldown += a + ",";
        });
        localStorage.setItem("folderDrilldown", drilldown);
    }

    public refresh(){
        this.vendorAccountService
            .getVendorAccountsByUsernameAndVendor({
                username: this.userPermissionService.loggedInUser!.username,
                vendorName: iManage,
            })
            .pipe(
                switchMap((res: any) => {
                    this.noBindings = res.length < 1;
                    if (!this.noBindings) return this.loadData();
                    return of();
                })
            );
    }

    finishImport(){
        this.dialogRef.close(true);
    }

    public getSelectedDocumentsList(workspaceId: string): DocumentDetails[] | undefined {
        return this.selectedDocumentsList.filter(doc => doc.workspace_id == workspaceId);
    }
}



interface CustomerData {
    customerId: {customerId: string},
    libraries: {libraryIds: string[]}
}

interface LibraryRow {
    libraryName: string,
    expanded: boolean
}

interface WorkspaceRow {
    authToken: string,
    customerId: string,
    libraryId: string,
    workspaceName: string,
    workspaceId: string,
    hasSubfolders: boolean,
    expanded: boolean
}

interface FolderRow{
    authToken: string, 
    customerId: string, 
    libraryId: string,
    folderName: string,
    folderId: string, 
    expanded: boolean
}

interface DocumentDetails {
    auth_token: string, 
    refresh_token: string,
    id: string,
    name: string,
    extension: string,
    workspace_id: string,
    workspace_name: string,
    fileName: string,
    size: string,
    customerId: string,
    libraryId: string, 
    selected: boolean,
    downloaded: number
}

